import { useState, type KeyboardEvent } from "react";
import { cn } from "@/lib/utils";
import { GlideTrack } from "./glide-highlight";

export type Segment = {
  value: string;
  label: string;
};

export function SegmentedControl({
  label,
  segments,
  value,
  defaultValue,
  onValueChange,
  className,
}: {
  label: string;
  segments: Segment[];
  value?: string;
  defaultValue?: string;
  onValueChange?: (value: string) => void;
  className?: string;
}) {
  const first = segments[0]?.value ?? "";
  const [uncontrolled, setUncontrolled] = useState(defaultValue ?? first);
  const selected = value ?? uncontrolled;

  function select(next: string) {
    if (value === undefined) setUncontrolled(next);
    onValueChange?.(next);
  }

  function onKeyDown(event: KeyboardEvent<HTMLButtonElement>, index: number) {
    const last = segments.length - 1;
    let next = index;
    if (event.key === "ArrowRight") next = index === last ? 0 : index + 1;
    else if (event.key === "ArrowLeft") next = index === 0 ? last : index - 1;
    else if (event.key === "Home") next = 0;
    else if (event.key === "End") next = last;
    else return;
    event.preventDefault();
    const segment = segments[next];
    if (!segment) return;
    select(segment.value);
    const tabs = event.currentTarget.parentElement?.querySelectorAll<HTMLButtonElement>("[role='radio']");
    tabs?.[next]?.focus();
  }

  return (
    <GlideTrack
      activeKey={selected}
      className={cn("inline-flex w-fit max-w-full gap-1 rounded-pill bg-surface-muted p-1", className)}
    >
      {(register) => (
        <div role="radiogroup" aria-label={label} className="inline-flex gap-1" data-slot="segmented-control">
          {segments.map((segment, index) => {
            const active = segment.value === selected;
            return (
              <button
                key={segment.value}
                ref={register(segment.value)}
                type="button"
                role="radio"
                aria-checked={active}
                tabIndex={active ? 0 : -1}
                className={cn(
                  "relative z-10 rounded-pill px-3.5 py-1.5 text-sm",
                  active ? "text-foreground" : "text-secondary",
                )}
                onClick={() => select(segment.value)}
                onKeyDown={(event) => onKeyDown(event, index)}
              >
                {segment.label}
              </button>
            );
          })}
        </div>
      )}
    </GlideTrack>
  );
}
